import { useCallback } from 'react';

import {
  Exact,
  GrantArchiveAccessMutation,
  RenewArchiveAccessMutation,
  RevokeArchiveAccessMutation
} from 'gql/graphql';
import useMutationWithAlert, { RunMutation } from 'hooks/graphql/useMutationWithAlert';

import { GrantArchiveAccess, RenewArchiveAccess, RevokeArchiveAccess } from './graphql';

type ArchiveAccessMethod = 'grant' | 'renew' | 'revoke';
type ArchiveAccessMutation =
  | GrantArchiveAccessMutation
  | RenewArchiveAccessMutation
  | RevokeArchiveAccessMutation;
type ArchiveAccessVariables = Exact<{
  labelId: string;
  artistId: string;
}>;

export type ArchiveAccessMutationApi = (
  archiveAccessMethod: ArchiveAccessMethod,
  labelId: string,
  artistId: string
) => ReturnType<RunMutation<ArchiveAccessMutation, ArchiveAccessVariables>>;

export default function useArchiveAccessMutations(): ArchiveAccessMutationApi {
  const [, grantAccess] = useMutationWithAlert(GrantArchiveAccess);
  const [, renewAccess] = useMutationWithAlert(RenewArchiveAccess);
  const [, revokeAccess] = useMutationWithAlert(RevokeArchiveAccess);

  const archiveAccess = useCallback<ArchiveAccessMutationApi>(
    (archiveAccessMethod, labelId, artistId) => {
      const args = { labelId, artistId };

      switch (archiveAccessMethod) {
        case 'grant':
          return grantAccess(args);
        case 'renew':
          return renewAccess(args);
        case 'revoke':
          return revokeAccess(args);
        default:
          throw new Error('Invalid archive access method');
      }
    },
    [grantAccess, renewAccess, revokeAccess]
  );

  return archiveAccess;
}
